import { Form } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { message, Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { Deployment } from '@/models/deployment'
import {
  getScalingPolicyPayload,
  getScalingPolicyValues,
  ScalingPolicyValues,
} from '@/pages/auto-deploy/scalingPolicy'
import {
  getDeploymentEditChanges,
  updateDeploymentPreservingConfig,
} from '@/pages/auto-deploy/updateDeployment'

import { ScalingPolicyFields } from './ScalingPolicyFields'

export default function EditScalingPolicyModal({
  open,
  onClose,
  deployment,
  onUpdate,
}: {
  open: boolean
  onClose: () => void
  deployment: Deployment
  onUpdate?: () => void
}) {
  const { t } = useTranslation('autoDeploy')
  const [form] = Form.useForm<ScalingPolicyValues>()
  const [updating, setUpdating] = useState(false)
  const [original, setOriginal] = useState(deployment)

  useEffect(() => {
    if (open) {
      setOriginal(deployment)
      form.resetFields()
      form.setFieldsValue(getScalingPolicyValues(deployment))
    }
    // Freeze the editing baseline while list polling replaces deployment objects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deployment.resID, form])

  const handleUpdate = async (values: ScalingPolicyValues) => {
    setUpdating(true)
    try {
      await updateDeploymentPreservingConfig(
        API,
        original.resID,
        getDeploymentEditChanges(
          original,
          getScalingPolicyPayload({ ...form.getFieldsValue(true), ...values }, original),
        ),
      )
      message.success(t('common.updateSuccess'))
      onClose()
      onUpdate?.()
    } catch (error) {
      console.error(error)
      message.error(t('common.updateFailed'))
    } finally {
      setUpdating(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={updating ? undefined : onClose}
      className="w-[640px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-32px)] overflow-y-auto"
    >
      <Modal.Header>{t('scalingPolicy.editTitle')}</Modal.Header>
      <Modal.Body>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          validateTrigger={['onChange', 'onBlur']}
          onFinish={handleUpdate}
        >
          <ScalingPolicyFields compact disabled={updating} savedMaximum={original.maxReplicas} />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} disabled={updating} className="min-w-[88px]">
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          onClick={() => form.submit()}
          disabled={updating}
          className="min-w-[88px]"
        >
          <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
